import { BooleanFilterSelect } from "@/components/form/components/BooleanFilterSelect";
import { InputNumber } from "@/components/InputNumber";
import { rangePickerFormItemProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import LabelWithTooltip from "../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../common/constants";
import { BooleanFilter } from "../../../../common/enums";
import { AgentDirectoratesSelect } from "../../../enumerations/components/form/AgentDirectoratesSelect";
import { ClientType, clientTypeTMap } from "../../enums";
import { ClientFilterPageRequest } from "../../types";
import { ClientTypeTag } from "../tags/ClientTypeTag";

export interface ExtendedClientFilterPageRequest extends ClientFilterPageRequest {
  createdAtRange?: string[];
}

type Props = {
  hidden?: boolean;
};

export const ClientExtendedFilterFormFields = ({ hidden }: Props) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<ExtendedClientFilterPageRequest>();
  const contactClientDaysMin = Form.useWatch("contactClientDaysMin", form);
  const contactClientDaysMax = Form.useWatch("contactClientDaysMax", form);
  const hasPartnerAgent = Form.useWatch("hasPartnerAgent", form);

  const isPartnerAgentFilterDisabled = hasPartnerAgent === BooleanFilter.NO;

  return (
    <>
      <Form.Item name="types" label={t("client.enums.type._label")} rules={[validations.none]} hidden={hidden}>
        <Select
          {...selectTagsStandardProps(clientTypeTMap)}
          allowClear
          mode="multiple"
          maxTagCount="responsive"
          tagRender={(props) => (
            <ClientTypeTag
              type={ClientType[props.value as keyof typeof ClientType]}
              closable={props.closable}
              onClose={props.onClose}
              label={props.label}
            />
          )}
          options={Object.values(ClientType).map((type) => ({
            value: type,
            label: <ClientTypeTag type={type} />,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="createdAtRange"
        label={t("client.filter.createdAtRange")}
        {...rangePickerFormItemProps()}
        hidden={hidden}
      >
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format={getDatePickerFormat()}
          maxDate={dayjs()}
          presets={getDatePickerDefaultRanges({ hasToday: true, maxDate: dayjs() })}
          placeholder={[t("common.from"), t("common.to")]}
        />
      </Form.Item>

      <Form.Item
        name="managedByDirectorateIds"
        label={t("client.filter.managedByDirectorateIds")}
        rules={[validations.none]}
        hidden={hidden}
      >
        <AgentDirectoratesSelect isMultiple allowClear showAllDirectorates />
      </Form.Item>

      <Form.Item
        name="hasPartnerAgent"
        label={t("client.enums.booleanFilterHasPartnerAgent._label")}
        rules={[validations.none]}
        hidden={hidden}
      >
        <BooleanFilterSelect
          translationPath="client.enums.booleanFilterHasPartnerAgent"
          allowClear
          onChange={(value) => {
            if (value === BooleanFilter.NO) {
              form.setFieldsValue({ partnerAgentDirectorateIds: undefined, partnerAgentIdNumber: undefined });
            }
          }}
        />
      </Form.Item>

      <Form.Item
        name="partnerAgentDirectorateIds"
        label={
          <LabelWithTooltip
            tooltip={t("client.helpers.partnerAgentDirectorateHint")}
            label={t("client.filter.partnerAgentDirectorateIds")}
          />
        }
        rules={[validations.none]}
        hidden={hidden}
      >
        <AgentDirectoratesSelect isMultiple allowClear disabled={isPartnerAgentFilterDisabled} showAllDirectorates />
      </Form.Item>

      <Form.Item
        name="partnerAgentIdNumber"
        label={
          <LabelWithTooltip
            tooltip={t("client.helpers.partnerAgentIdNumberHint")}
            label={t("client.filter.partnerAgentIdNumber")}
          />
        }
        rules={[validations.size(1, 64)]}
        hidden={hidden}
      >
        <Input allowClear disabled={isPartnerAgentFilterDisabled} />
      </Form.Item>

      <div style={{ display: hidden ? "none" : undefined }}>
        <div className="margin-bottom-tiny">{t("client.filter.contactClientDays")}</div>

        <Flex gap={rowGutter}>
          <Form.Item
            name="contactClientDaysMin"
            rules={[validations.maxNumber(contactClientDaysMax)]}
            dependencies={["contactClientDaysMax"]}
          >
            <InputNumber placeholder={t("common.from")} />
          </Form.Item>

          <Form.Item
            name="contactClientDaysMax"
            rules={[validations.minNumber(contactClientDaysMin)]}
            dependencies={["contactClientDaysMin"]}
          >
            <InputNumber placeholder={t("common.to")} />
          </Form.Item>
        </Flex>
      </div>
    </>
  );
};
